import { useContext, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useQueries,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  BodyWrapper,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
  Message,
} from '@components';
import { GlobalContext } from '@contexts';
import { usePageTemplates } from '@hooks';
import {
  datamodelQuery,
  pageDeleteMutation,
  pageMutation,
  pageQuery,
  siteQuery,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import { PageForm } from '@components';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';
import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import { errorsFromMutate } from '@Forms/utils.js';
const TemplatePageWrapper = ({ pageData, siteData, ...props }) => {
  // load some additional data needed to render the page form for a template page
  const { data: pageTemplates, isPending: pageTemplatesIsPending } =
    usePageTemplates(siteData.id, siteData.theme_id);

  // filter templates to the one tied to this page
  const filteredPageTemplates = !pageTemplatesIsPending
    ? pageTemplates.filter((p) => p.template_name == pageData.custom_page_type)
    : [];

  // get the datamodel
  const { data: datamodelData, isPending: datamodelIsPending } = useQuery(
    datamodelQuery(
      !pageTemplatesIsPending && filteredPageTemplates.length > 0
        ? filteredPageTemplates[0].datamodel_id
        : null,
    ),
  );

  // get all the related datamodels used on fields
  const relatedDatamodels = useQueries({
    enabled: !datamodelIsPending,
    queries: datamodelData
      ? datamodelData.fields
          .filter((field) => Number.isInteger(field.related_datamodel_id))
          .map((field) => datamodelQuery(field.related_datamodel_id))
      : [],
  });
  // are all datamodel queries finished
  const allFinished = relatedDatamodels.every((query) => query.isSuccess);

  if (!pageTemplatesIsPending && filteredPageTemplates.length == 0) {
    return (
      <Message
        messageStyle="error"
        title="Page template error"
        message={`Can't find matching Page template for ${pageData.custom_page_type}`}
        fadeoutMessage={false}
        isFormMessage={false}
      />
    );
  }

  return (
    <>
      {(pageTemplatesIsPending || datamodelIsPending || !allFinished) && (
        <Loader className="sb-loader sb-loader-large" />
      )}
      {pageTemplates && datamodelData && allFinished && (
        <PageForm
          {...props}
          pageTemplate={filteredPageTemplates[0]}
          datamodelData={datamodelData}
          relatedDatamodels={relatedDatamodels}
          pageData={pageData}
          siteData={siteData}
        />
      )}
    </>
  );
};

export const SitePage = () => {
  const { t, i18n } = useTranslation();
  const { pageID, siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const globals = useContext(GlobalContext);
  const [errorMessage, setErrorMessage] = useState(null);
  const errorMessageRef = useRef(null);

  // scroll to error message when it's set
  useEffect(() => {
    if (errorMessage && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [errorMessage]);
  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // get page
  const {
    data: pageData,
    isPending: pageIsPending,
    isError,
  } = useQuery(pageQuery(pageID));

  const onPageSaveSuccess = (data) => {
    // seed the detail cache with the known-good server response so pageData
    // (and PageForm's `values: pageData` sync) updates immediately, rather
    // than waiting on a separate invalidate+refetch round-trip.
    if (data) {
      queryClient.setQueryData(['pages', 'detail', pageID, {}], data);
    }
    // invalidate the lists so its purged
    queryClient.invalidateQueries({ queryKey: ['pages', 'list'] });
  };

  // save page
  const mutation = useMutation(
    pageMutation(pageID, {
      onMutate: (data) => {
        setErrorMessage(null);
      },
      onSuccess: onPageSaveSuccess,
      onError: (error) => {
        const errorsList = errorsFromMutate(error);
        const allError = errorsList.find(({ field }) => field === '__all__');
        if (!!allError) {
          setErrorMessage(allError.error.message);
        }
      },
    }),
  );

  // publish page
  const publishMutation = useMutation(
    pageMutation(pageID, {
      onMutate: (data) => {
        setErrorMessage(null);
      },
      onSuccess: onPageSaveSuccess,
      onError: (error) => {
        const errorsList = errorsFromMutate(error);
        const allError = errorsList.find(({ field }) => field === '__all__');
        if (!!allError) {
          setErrorMessage(allError.error.message);
        }
      },
    }),
  );

  // delete page
  const deleteMutation = useMutation(
    pageDeleteMutation(pageID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['pages', 'list'] });
        // navigate to the list view
        navigate(`/dashboard/sites/${siteID}/pages`);
      },
    }),
  );

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <Link to={`/dashboard/sites/${siteID}/pages`}>{t('Pages')}</Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              {pageIsPending && <span>...</span>}
              {pageData && <span>{pageData.name}</span>}
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            {(siteIsPending || pageIsPending) && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}
            {errorMessage && (
              <div ref={errorMessageRef}>
                <Message
                  closable={true}
                  messageStyle="error"
                  title={'Something Went Wrong'}
                  isFormMessage={false}
                  message={errorMessage}
                />
              </div>
            )}
            {siteData && pageData && !isError && (
              <>
                <h1>{pageData.name}</h1>
                {pageData.type == 'template_page' && (
                  <TemplatePageWrapper
                    disabled={mutation.isPending || publishMutation.isPending}
                    mutation={mutation}
                    publishMutation={publishMutation}
                    pageData={pageData}
                    siteData={siteData}
                  />
                )}
                <div className="sb-message-action-section">
                  <ActionObject
                    onAction={() => deleteMutation.mutate()}
                    loading={deleteMutation.isPending}
                    actionName="Delete"
                    actionVerb="Deleting"
                    label="Delete page"
                    note="This page will be permanently deleted"
                  />
                </div>
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
